/**
 * Packets API endpoints
 */

import { apiClient } from './client';
import { z } from 'zod';
import { paginatedResponse } from './schemas';
import { parseResponse } from '@/lib/parseResponse';
import {
  bulkArchive,
  bulkUnarchive,
  bulkChangeOwner,
  bulkAddLabels,
  bulkRemoveLabels,
} from './bulkOperations';
import { getLabels, deleteLabel } from './labels';
import type {
  Packet,
  PacketComponent,
  PacketListParams,
  PacketListResponse,
  PacketCreatePayload,
  PacketUpdatePayload,
  PacketLabelCategory,
  PacketLabelCategoryWithLabels,
  PacketAnalytics,
  ComponentAnalytics,
} from '@/types';

// F9: PURE — returns a new object instead of mutating in place.
function withShareCompat(packet: Packet): Packet {
  if (packet.shares?.length && !packet.share) {
    return { ...packet, share: packet.shares[0] };
  }
  return packet;
}

// F9: schema for the packet-list envelope.
const PacketListSchema = paginatedResponse(z.record(z.string(), z.unknown()));

/** The packets list endpoint (list GET and the bulk select-all request_url). */
export const PACKETS_LIST_PATH = '/api/packets/';

/** The query params the list GET sends: ONE definition, shared with the bulk request_url. */
export function packetListWireParams(params: PacketListParams = {}): Record<string, unknown> {
  const { labels, ...rest } = params;
  return {
    ...rest,
    ...(labels?.length && { labels: labels.join(',') }),
  };
}

export async function getPackets(params: PacketListParams = {}): Promise<PacketListResponse> {
  const response = await apiClient.get<unknown>(PACKETS_LIST_PATH, {
    params: packetListWireParams(params),
  });
  // F9: boundary-validate via parseResponse.
  const parsed = parseResponse(PacketListSchema, response.data);
  const results = (parsed.results as unknown as Packet[]).map(withShareCompat);
  return { ...parsed, results } as unknown as PacketListResponse;
}

// Get single packet by ID
export async function getPacket(id: number): Promise<Packet> {
  const response = await apiClient.get<Packet>(`/api/packets/${id}/`);
  return withShareCompat(response.data);
}

// Create new packet. Copy/revise ride in the body (PacketCreateInput:
// packet_id/copy/revise, snake_cased by the client) — the route declares no
// query params, and vingapi 422s undeclared ones.
export async function createPacket(payload: PacketCreatePayload = {}): Promise<Packet> {
  const response = await apiClient.post<Packet>('/api/packets/', payload);
  return withShareCompat(response.data);
}

/**
 * Seconds → minutes for every certificate component in an outgoing payload.
 *
 * vingapi reads a certificate component's `duration` as MINUTES (it stores
 * `duration * 60`) but returns SECONDS, and every packet PUT re-sends the
 * WHOLE components array. Handing back the seconds we were given therefore
 * re-multiplies each certificate by 60 on every save — a valid 60-minute
 * certificate becomes 60 hours, then 150 days, and vingapi's thirty-day
 * ceiling 422s the packet from then on (#123, #4182). Converting here, at the
 * single request boundary, means no caller has to remember the unit: local
 * state carries the server's seconds everywhere, and only the wire is minutes.
 */
function toWireComponents(components: PacketComponent[]): PacketComponent[] {
  return components.map((component) =>
    component.type === 'certificate' && typeof component.duration === 'number'
      ? { ...component, duration: Math.round(component.duration / 60) }
      : component,
  );
}

// Update packet
export async function updatePacket(id: number, payload: PacketUpdatePayload): Promise<Packet> {
  const body: PacketUpdatePayload = payload.components
    ? { ...payload, components: toWireComponents(payload.components) }
    : payload;
  const response = await apiClient.put<Packet>(`/api/packets/${id}/`, body);
  return withShareCompat(response.data);
}

// ---------------------------------------------------------------------------
// Component-scoped PATCH (vingapi#913) — replace a failed file, edit text.
// The body accepts only the non-derived fields; the response is the full
// PacketDetail (capabilities included) so one refetch is saved.
// ---------------------------------------------------------------------------

/**
 * Allowed body fields for `PATCH /api/packets/{id}/components/{cid}/`.
 * Every field is optional (absent != null). Derived/read-only fields
 * (duration, file, id, type, position, premium, scored, lock_position,
 * deletable, page_count) are rejected with 422.
 */
/** A question row in a component PATCH — id + text fields only. */
export interface ComponentPatchQuestion {
  id: number;
  text?: string;
  choices?: Array<{ id: number; text: string; correct?: boolean }>;
}

export interface ComponentPatchBody {
  title?: string | null;
  text?: string | null;
  link_url?: string | null;
  certificate_key?: string | null;
  file_key?: string | null;
  questions?: ComponentPatchQuestion[] | null;
}

/** Fields the server rejects on a component PATCH (400 if sent). */
export const COMPONENT_PATCH_DERIVED_FIELDS = [
  'duration', 'file', 'id', 'type', 'position', 'premium',
  'file_id', 'scored', 'lock_position', 'deletable', 'page_count',
] as const;

export async function patchPacketComponent(
  packetId: number,
  componentId: number,
  body: ComponentPatchBody,
): Promise<Packet> {
  const response = await apiClient.patch<Packet>(
    `/api/packets/${packetId}/components/${componentId}/`,
    body,
  );
  return withShareCompat(response.data);
}

// ---------------------------------------------------------------------------
// Title/summary update (plan v5) — PUT with exactly {title, summary}.
// A dedicated function so no call site can carry components/questions/status.
// ---------------------------------------------------------------------------

/** Closed body for the title/summary PUT — both keys always sent. */
export interface TitleSummaryBody {
  title: string;
  summary: string;
}

export async function updatePacketTitleSummary(
  id: number,
  body: TitleSummaryBody,
): Promise<Packet> {
  const response = await apiClient.put<Packet>(`/api/packets/${id}/`, body);
  return withShareCompat(response.data);
}

/** A field-level rejection vingapi reported for one component (HTTP 422). */
export interface ComponentFieldError {
  /** The component the API blamed, when it named one. */
  componentId: number | null;
  /** The offending field, e.g. `duration`. */
  field: string;
  /** The API's own message — shown verbatim, never paraphrased. */
  message: string;
}

/**
 * Read a component field error off a rejected request.
 *
 * vingapi's ComponentValidationError serializes as
 * `{detail, field, component_id}` with status 422. Error bodies do NOT pass
 * through the client's camelCase conversion (that runs on the success path
 * only), so `component_id` arrives snake_cased; both spellings are accepted
 * so a future change to that can't silently drop the error on the floor.
 *
 * Returns null for anything that isn't a field-tagged 422 — the caller then
 * falls back to its generic failure handling.
 */
export function parseComponentFieldError(error: unknown): ComponentFieldError | null {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)
    ?.response;
  if (!response || response.status !== 422) return null;

  const data = response.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const body = data as Record<string, unknown>;

  const field = typeof body.field === 'string' && body.field ? body.field : null;
  const message = typeof body.detail === 'string' && body.detail ? body.detail : null;
  if (!field || !message) return null;

  const rawId = body.component_id ?? body.componentId;
  const numericId = typeof rawId === 'number' ? rawId : Number(rawId);
  const componentId = Number.isFinite(numericId) && numericId !== 0 ? numericId : null;

  return { componentId, field, message };
}

// Delete packet
export async function deletePacket(id: number): Promise<void> {
  await apiClient.delete(`/api/packets/${id}/`);
}

// Publish packet (update status to published)
export async function publishPacket(id: number): Promise<Packet> {
  return updatePacket(id, { status: 'published' });
}

const PACKETS_ENDPOINT = '/api/packets/';

// Archive packets (bulk)
export const archivePackets = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean
): Promise<void> =>
  bulkArchive(PACKETS_ENDPOINT, { selectedIds, excludedIds, selectedAll });

// Unarchive packets (bulk)
export const unarchivePackets = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean
): Promise<void> =>
  bulkUnarchive(PACKETS_ENDPOINT, { selectedIds, excludedIds, selectedAll });

// Change owner (bulk)
export const changePacketOwner = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  ownerId: string
): Promise<void> =>
  bulkChangeOwner(
    PACKETS_ENDPOINT,
    { selectedIds, excludedIds, selectedAll },
    parseInt(ownerId, 10)
  );

// Add labels (bulk)
export const addPacketLabels = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  labels: string[]
): Promise<void> =>
  bulkAddLabels(PACKETS_ENDPOINT, { selectedIds, excludedIds, selectedAll }, labels);

// Remove labels (bulk)
export const removePacketLabels = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  labels: string[]
): Promise<void> =>
  bulkRemoveLabels(PACKETS_ENDPOINT, { selectedIds, excludedIds, selectedAll }, labels);

// Get packet labels (categorized) - uses shared labels utility
export const getPacketLabels = (): Promise<PacketLabelCategoryWithLabels[]> =>
  getLabels('packet').then((categories) =>
    categories.map((cat) => ({
      name: cat.name,
      labels: cat.labels.map((l) => ({ name: l.name, count: l.objectCount })),
    }))
  );

// Delete packet label - uses shared labels utility
export const deletePacketLabel = (label: string): Promise<void> =>
  deleteLabel('packet', label);

// Create label category
export async function createPacketLabelCategory(
  category: string,
  labels: string[]
): Promise<PacketLabelCategory> {
  const response = await apiClient.post<PacketLabelCategory>(
    `/api/labels/categories/packet/${encodeURIComponent(category)}/`,
    { labels }
  );
  return response.data;
}

// Delete label category
export async function deletePacketLabelCategory(category: string): Promise<void> {
  await apiClient.delete(`/api/labels/categories/packet/${encodeURIComponent(category)}/`);
}

// Get packet analytics
export async function getPacketAnalytics(packetId: number): Promise<PacketAnalytics> {
  const response = await apiClient.get<PacketAnalytics>(
    `/api/data/assignments/packet/${packetId}/`
  );
  return response.data;
}

// Get component analytics
export async function getComponentAnalytics(
  packetId: number,
  componentId: number
): Promise<ComponentAnalytics> {
  const response = await apiClient.get<ComponentAnalytics>(
    `/api/data/assignments/packet/${packetId}/component/${componentId}/`
  );
  return response.data;
}

// Re-export unsent packet invitations from invitations module (properly typed)
export { listUnsentPacketInvitations as getUnsentPacketInvitations } from './invitations';

// Copy packet
export async function copyPacket(packetId: number): Promise<Packet> {
  return createPacket({ packetId, copy: true });
}

// Revise packet (create new revision)
export async function revisePacket(
  packetId: number,
): Promise<Packet> {
  return createPacket({
    packetId,
    revise: true,
  });
}

// ========================================
// Component CRUD Operations
// ========================================

export interface ComponentCreateData {
  /** Type - required for non-file components (text, questions, link, certificate) */
  type?: string;
  title?: string;
  text?: string;
  /** File key - when present, backend infers type from file (don't send type) */
  fileKey?: string;
  linkUrl?: string;
}

/**
 * Add a component to packet.
 *
 * Frontend is the source of truth during editing. This function takes
 * the current components from local state and appends the new one.
 * No GET - just PUT what we have.
 *
 * For file uploads: only send fileKey, backend infers type from file
 * For non-file: send type (text, questions, link, certificate)
 *
 * @param packetId - Packet ID
 * @param currentComponents - Current components from local state
 * @param newComponentData - Data for the new component
 */
export async function addComponent(
  packetId: number,
  currentComponents: PacketComponent[],
  newComponentData: ComponentCreateData
): Promise<Packet> {
  // Build new component - only include fields that are set
  const newComponent: Partial<PacketComponent> = {};

  // For file uploads, don't send type - backend infers from file
  // For non-file components, type is required
  if (newComponentData.fileKey) {
    newComponent.fileKey = newComponentData.fileKey;
  } else if (newComponentData.type) {
    newComponent.type = newComponentData.type;
  }

  if (newComponentData.title) newComponent.title = newComponentData.title;
  if (newComponentData.text !== undefined) newComponent.text = newComponentData.text;
  if (newComponentData.linkUrl) newComponent.linkUrl = newComponentData.linkUrl;

  return updatePacket(packetId, {
    components: [...currentComponents, newComponent as PacketComponent],
  });
}

/**
 * Delete a component from packet.
 *
 * @param packetId - Packet ID
 * @param currentComponents - Current components from local state
 * @param componentId - ID of component to delete
 */
export async function deleteComponent(
  packetId: number,
  currentComponents: PacketComponent[],
  componentId: number
): Promise<Packet> {
  const components = currentComponents.filter((c) => c.id !== componentId);
  return updatePacket(packetId, { components });
}

/**
 * Reorder components within a packet.
 *
 * @param packetId - Packet ID
 * @param reorderedComponents - Components in new order (from local state after drag-drop)
 */
export async function reorderComponents(
  packetId: number,
  reorderedComponents: PacketComponent[]
): Promise<Packet> {
  return updatePacket(packetId, { components: reorderedComponents });
}
